/**
 * VersionHistory - Container component for document version history
 *
 * Manages state for version selection and coordinates between
 * VersionSidebar and VersionContent components. Supports compare
 * mode to show diffs between two selected versions.
 */
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { VersionSidebar } from "./VersionSidebar";
import { VersionContent } from "./VersionContent";
import { VersionDiff } from "./VersionDiff";
import { useVersionsApi } from "../../../lib/api/versions";
import type { DocumentVersionDetail } from "../../../types/version";

interface VersionHistoryProps {
  /** Document ID to show versions for */
  documentId: number;
  /** Current document title (for display) */
  documentTitle?: string;
}

export function VersionHistory({ documentId, documentTitle }: VersionHistoryProps) {
  const { getVersions, getVersion } = useVersionsApi();
  const [selectedVersionNumber, setSelectedVersionNumber] = useState<number | null>(null);
  const [selectedVersionDetail, setSelectedVersionDetail] = useState<DocumentVersionDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Compare mode state
  const [compareMode, setCompareMode] = useState(false);
  const [compareVersions, setCompareVersions] = useState<number[]>([]);
  const [compareVersionDetails, setCompareVersionDetails] = useState<{
    old: DocumentVersionDetail | null;
    new: DocumentVersionDetail | null;
  }>({ old: null, new: null });
  const [isLoadingCompare, setIsLoadingCompare] = useState(false);

  // Fetch version list - refetch on mount to get latest versions
  const {
    data: versionsData,
    isLoading: isLoadingVersions,
    error: versionsError,
  } = useQuery({
    queryKey: ["document-versions", documentId],
    queryFn: () => getVersions({ documentId, pageSize: 100 }),
    enabled: !!documentId,
    refetchOnMount: "always",
    staleTime: 0,
  });

  // Auto-select the most recent version when versions load
  useEffect(() => {
    if (versionsData?.versions && versionsData.versions.length > 0 && selectedVersionNumber === null) {
      const mostRecent = Math.max(...versionsData.versions.map(v => v.version_number));
      setSelectedVersionNumber(mostRecent);
    }
  }, [versionsData, selectedVersionNumber]);

  // Fetch version detail when selection changes (non-compare mode)
  useEffect(() => {
    if (compareMode) return;

    if (selectedVersionNumber === null) {
      setSelectedVersionDetail(null);
      return;
    }

    const fetchVersionDetail = async () => {
      setIsLoadingDetail(true);
      setDetailError(null);
      try {
        const detail = await getVersion(documentId, selectedVersionNumber);
        setSelectedVersionDetail(detail);
      } catch (err) {
        setDetailError(
          err instanceof Error ? err.message : "Failed to load version"
        );
        setSelectedVersionDetail(null);
      } finally {
        setIsLoadingDetail(false);
      }
    };

    fetchVersionDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, selectedVersionNumber, compareMode]);

  // Fetch both version details when compare mode has 2 selections
  useEffect(() => {
    if (!compareMode || compareVersions.length !== 2) {
      setCompareVersionDetails({ old: null, new: null });
      return;
    }

    const fetchCompareVersions = async () => {
      setIsLoadingCompare(true);
      try {
        // Sort to get old (lower number) and new (higher number)
        const sorted = [...compareVersions].sort((a, b) => a - b);
        const [oldV, newV] = await Promise.all([
          getVersion(documentId, sorted[0]),
          getVersion(documentId, sorted[1]),
        ]);
        setCompareVersionDetails({ old: oldV, new: newV });
      } catch (err) {
        console.error("Failed to load compare versions:", err);
        setCompareVersionDetails({ old: null, new: null });
      } finally {
        setIsLoadingCompare(false);
      }
    };

    fetchCompareVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, compareMode, compareVersions]);

  const handleSelectVersion = (versionNumber: number) => {
    setSelectedVersionNumber(versionNumber);
  };

  const handleToggleCompareMode = () => {
    setCompareMode(!compareMode);
    setCompareVersions([]);
    setCompareVersionDetails({ old: null, new: null });
  };

  const handleToggleCompareVersion = (versionNumber: number) => {
    setCompareVersions((prev) => {
      if (prev.includes(versionNumber)) {
        return prev.filter((v) => v !== versionNumber);
      }
      if (prev.length < 2) {
        return [...prev, versionNumber];
      }
      return prev;
    });
  };

  if (versionsError) {
    return (
      <div className="flex-1 flex items-center justify-center bg-brand-surface">
        <div className="text-center p-6">
          <p className="text-brand-error font-medium">Failed to load versions</p>
          <p className="text-brand-muted text-sm mt-1">
            {versionsError instanceof Error ? versionsError.message : "Unknown error"}
          </p>
        </div>
      </div>
    );
  }

  // Determine what to show in the content area
  const renderContent = () => {
    if (compareMode) {
      if (compareVersions.length < 2) {
        return (
          <div className="flex-1 p-6 bg-brand-bg flex items-center justify-center">
            <div className="text-center text-brand-muted">
              <p className="text-lg font-medium">Select two versions to compare</p>
              <p className="text-sm text-brand-muted mt-1">
                Click on versions in the sidebar to select them
              </p>
            </div>
          </div>
        );
      }

      if (isLoadingCompare) {
        return (
          <div className="flex-1 p-6 bg-brand-bg">
            <div className="max-w-3xl mx-auto space-y-6">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-brand-surface rounded w-3/4" />
                <div className="h-4 bg-brand-surface rounded w-1/4" />
                <div className="h-px bg-brand-border my-4" />
                <div className="space-y-3">
                  <div className="h-4 bg-brand-surface rounded" />
                  <div className="h-4 bg-brand-surface rounded" />
                  <div className="h-4 bg-brand-surface rounded w-5/6" />
                </div>
              </div>
            </div>
          </div>
        );
      }

      if (compareVersionDetails.old && compareVersionDetails.new) {
        return (
          <VersionDiff
            oldVersion={compareVersionDetails.old}
            newVersion={compareVersionDetails.new}
          />
        );
      }
    }

    return (
      <VersionContent
        version={selectedVersionDetail}
        isLoading={isLoadingDetail}
        error={detailError}
      />
    );
  };

  return (
    <div className="flex h-full">
      <VersionSidebar
        versions={versionsData?.versions || []}
        selectedVersion={selectedVersionNumber}
        onSelectVersion={handleSelectVersion}
        isLoading={isLoadingVersions}
        currentVersionCount={versionsData?.total || 0}
        compareMode={compareMode}
        onToggleCompareMode={handleToggleCompareMode}
        compareVersions={compareVersions}
        onToggleCompareVersion={handleToggleCompareVersion}
      />
      {renderContent()}
    </div>
  );
}
